import React, { useState, useEffect, useRef } from 'react';
import Icon from './icons/Icon';
import Checkbox from './Checkbox';
import { TodoTagEntity } from '../types/data';
import './InputTodoTags.css';

export interface InputTodoTagsProps {
  /** 現在選択されているタグIDs */
  selectedTagIds?: string[];
  /** 候補として表示するタグ一覧 */
  availableTags: TodoTagEntity[];
  /** 表示状態 */
  isOpen: boolean;
  /** タグの選択状態が変更された時のコールバック */
  onTagSelectionChange: (selectedTagIds: string[]) => void;
  /** 新規タグ作成時のコールバック */
  onAddNewTag?: (title: string) => void;
  /** コンポーネントを閉じる時のコールバック */
  onClose?: () => void;
  /** 開いた時に検索入力へ自動フォーカスするか */
  autoFocusInput?: boolean;
}

const InputTodoTags: React.FC<InputTodoTagsProps> = ({
  selectedTagIds = [],
  availableTags,
  isOpen,
  onTagSelectionChange,
  onAddNewTag,
  onClose,
  autoFocusInput = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTags, setFilteredTags] = useState<TodoTagEntity[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isComposing, setIsComposing] = useState(false); // IME変換中かどうかのフラグ
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 選択されているタグオブジェクトを取得
  const selectedTags = availableTags.filter(tag => selectedTagIds.includes(tag.id));

  // タグフィルタリング（検索語が変わるたびに再計算）
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTags(availableTags);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredTags(
        availableTags.filter(tag => tag.title.toLowerCase().includes(lower))
      );
    }
  }, [searchTerm, availableTags]);

  // タグの選択/選択解除
  const handleTagToggle = (tagId: string) => {
    const newSelectedIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];
    onTagSelectionChange(newSelectedIds);
  };

  // タグチップの削除
  const handleRemoveTag = (tagId: string) => {
    const newSelectedIds = selectedTagIds.filter(id => id !== tagId);
    onTagSelectionChange(newSelectedIds);
  };

  // 新規タグ追加
  const handleAddNewTag = () => {
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm && onAddNewTag) {
      onAddNewTag(trimmedTerm);
      setSearchTerm('');
      // 複数選択を続けられるようにドロップダウンは閉じない
      setShowDropdown(true);
    }
  };

  // 入力フィールドのフォーカス処理
  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // 入力フィールドのブラーでは閉じない（複数選択用に開いたままにする）
  const handleInputBlur = () => {
    // no-op
  };

  // IME変換開始
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  // IME変換終了
  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  // Enterキーでの新規タグ作成
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // IME変換中は何もしない（変換確定のみ）
      if (isComposing) {
        return;
      }
      
      // IME変換中でない場合のみタグ作成処理
      if (searchTerm.trim() !== '') {
        e.preventDefault();
        handleAddNewTag();
      }
    }
    // Escape は処理しない（Drawer 側で処理する）
  };

  // 検索語の変更
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  // コンポーネントが閉じた時に検索語をクリア
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setShowDropdown(false);
    } else if (autoFocusInput) {
      // 開いた時は入力フィールドにフォーカス
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, autoFocusInput]);

  // コンテナ外クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showDropdown && containerRef.current && !containerRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  if (!isOpen) {
    return null;
  }

  return (
    <div ref={containerRef} className="input-todo-tags">
      <div className="input-todo-tags__container">
        {/* エリアA: 入力フィールド + 選択済みタグチップ */}
        <div className="input-todo-tags__input-area">
          {/* 選択済みタグのチップ */}
          <div className="input-todo-tags__selected-chips">
            {selectedTags.map(tag => (
              <div key={tag.id} className="input-todo-tags__chip">
                <span className="input-todo-tags__chip-text text-single-small">{tag.title}</span>
                <button
                  type="button"
                  className="input-todo-tags__chip-remove"
                  onClick={() => handleRemoveTag(tag.id)}
                  aria-label={`${tag.title}を削除`}
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            ))}
          </div>
          
          {/* 検索入力フィールド */}
          <input
            ref={inputRef}
            type="text"
            className="input-todo-tags__search-input text-single-medium"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder="タグを検索または作成..."
          />
        </div>

        {/* エリアB: ドロップダウン候補リスト */}
        {showDropdown && (
          <div className="input-todo-tags__dropdown">
            <div className="input-todo-tags__dropdown-list">
              {filteredTags.map(tag => (
                <div
                  key={tag.id}
                  className="input-todo-tags__dropdown-item"
                  onClick={() => handleTagToggle(tag.id)}
                >
                  <Checkbox
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={() => handleTagToggle(tag.id)}
                  />
                  <span className="input-todo-tags__dropdown-item-title text-single-medium">{tag.title}</span>
                </div>
              ))}

              {/* 新規タグ作成ボタン */}
              {searchTerm.trim() !== '' && (
                <button
                  type="button"
                  className="input-todo-tags__add-new"
                  onClick={handleAddNewTag}
                >
                  <Icon name="add" size={16} />
                  <span className="text-single-medium">{searchTerm} をタグに追加</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputTodoTags;
